import _ from 'lodash';
import React, { useMemo } from 'react';
import { renderToString } from 'react-dom/server';
import { withI18n } from '@shopify/react-i18n';
import { connect } from 'react-redux';
import * as FileSaver from 'file-saver';
import beautify from 'simply-beautiful';
import classNames from 'classnames';
import {
  Button,
  ButtonGroup,
  Icon,
  Link,
  Modal,
  TextStyle,
  SkeletonThumbnail,
  SkeletonDisplayText
} from '@shopify/polaris';
import { ExternalMinor } from '@shopify/polaris-icons';
import { MAP_MODAL_TITLE, css } from 'src/constants/klaviyo';
import useGetRecommendProducts from './useGetRecommendProducts';
import { pickImageNatural } from 'src/helpers/image-helper';
import { formatMoney } from 'src/helpers/money-format';
import { copyClipBoard, copyClipBoardNavigator } from 'src/helpers/shopify-integration-helpers';
import * as productAction from 'actions/product-actions';

interface ComposedPropsItem {
  onClose: () => void;
  updateRecommendProducts: (recommendationType: string, products: any) => void;
  isOpen: boolean;
  recommendProducts: any;
  recommendationType: string;
}

const PreviewProductsModal: React.FC<ComposedPropsItem> = ({
  onClose,
  isOpen,
  recommendationType,
  moneyFormat,
  recommendProducts,
  updateRecommendProducts,
  i18n
}) => {
  const { isLoading } = useGetRecommendProducts(
    recommendationType,
    recommendProducts,
    updateRecommendProducts
  );

  const renderPreview = useMemo(() => {
    return (
      <div className="preview-content">
        <p className="title-type">
          {MAP_MODAL_TITLE[recommendationType]
            ? String(i18n.translate(MAP_MODAL_TITLE[recommendationType])).toUpperCase()
            : ''}{' '}
          {String(
            i18n.translate('AdminHTML.KlaviyoIntegration.PreviewProductsModal.products')
          ).toUpperCase()}
        </p>

        <div className="product-container">
          {isLoading &&
            [1, 2, 3, 4].map(item => {
              return (
                <div className="product-item" key={item}>
                  <div className="product-item__thumbnail">
                    <SkeletonThumbnail />
                  </div>
                  <div className="product-item__info">
                    <SkeletonDisplayText />
                    <div className="product-item__info__price-wrapper">
                      <SkeletonDisplayText />
                    </div>
                  </div>
                </div>
              );
            })}

          {!!recommendProducts[recommendationType]?.length &&
            !isLoading &&
            recommendProducts[recommendationType].map((item, index) => {
              const imageMainProduct = pickImageNatural(item.images);
              const price = Number(item.variants[0]?.price) || item.price_min || item.price_max;
              const compareAtPrice =
                Number(item.variants[0]?.compare_at_price) ||
                item.compare_at_price_min ||
                item.compare_at_price_max;

              return (
                <div
                  className={classNames('product-item', {
                    'product-item--margin-left': index === 1 || index === 3,
                    'product-item--margin-top': index === 2 || index === 3
                  })}
                  key={item.id}>
                  <div className="product-item__thumbnail">
                    <img src={imageMainProduct} alt="thumbnail" />
                  </div>
                  <div className="product-item__info">
                    <p className="product-item__info__title">{item.title}</p>
                    <div className="product-item__info__price-wrapper">
                      <span className="product-item__info__price">
                        {formatMoney(price, moneyFormat)}
                      </span>
                      {!_.isNil(compareAtPrice) && compareAtPrice > price && (
                        <span className="product-item__info__compare-price">
                          {formatMoney(price, moneyFormat)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    );
  }, [i18n, isLoading, moneyFormat, recommendProducts, recommendationType]);

  const renderCopyPreview = useMemo(() => {
    const renderRecommendProducts = [];
    if (!!recommendProducts[recommendationType]?.length && !isLoading) {
      for (let i = 0; i < recommendProducts[recommendationType].length; i += 2) {
        const itemFirst = recommendProducts[recommendationType][i];
        const imageMainProductFirst = pickImageNatural(itemFirst.images);
        const priceFirst =
          Number(itemFirst.variants[0]?.price) || itemFirst.price_min || itemFirst.price_max;
        const compareAtPriceFirst =
          Number(itemFirst.variants[0]?.compare_at_price) ||
          itemFirst.compare_at_price_min ||
          itemFirst.compare_at_price_max;

        const itemSecond = recommendProducts[recommendationType][i + 1];
        const imageMainProductSecond = pickImageNatural(itemSecond.images);
        const priceSecond =
          Number(itemSecond.variants[0]?.price) || itemSecond.price_min || itemSecond.price_max;
        const compareAtPriceSecond =
          Number(itemSecond.variants[0]?.compare_at_price) ||
          itemSecond.compare_at_price_min ||
          itemSecond.compare_at_price_max;

        renderRecommendProducts.push(
          <div style={{ margin: '0px auto', maxWidth: 600 }} key={i}>
            <table
              align="center"
              border={0}
              cellPadding={0}
              cellSpacing={0}
              role="presentation"
              style={{ width: '100%' }}>
              <tbody>
                <tr>
                  <td
                    style={{
                      direction: 'ltr',
                      fontSize: 0,
                      padding: '20px 0',
                      textAlign: 'center'
                    }}>
                    <div
                      className="mj-column-per-50 mj-outlook-group-fix"
                      style={{
                        fontSize: 0,
                        textAlign: 'left',
                        direction: 'ltr',
                        display: 'inline-block',
                        verticalAlign: 'top',
                        width: '100%'
                      }}>
                      <table
                        border={0}
                        cellPadding={0}
                        cellSpacing={0}
                        role="presentation"
                        style={{ verticalAlign: 'top' }}
                        width="100%">
                        <tbody>
                          <tr>
                            <td
                              align="center"
                              style={{
                                fontSize: 0,
                                padding: '10px 25px',
                                wordBreak: 'break-word'
                              }}>
                              <div
                                style={{
                                  fontSize: 13,
                                  lineHeight: 1,
                                  textAlign: 'center',
                                  color: '#000000'
                                }}>
                                <img
                                  src={imageMainProductFirst}
                                  style={{
                                    width: 160,
                                    height: 160,
                                    objectFit: 'contain',
                                    margin: 'auto',
                                    objectPosition: 'center'
                                  }}
                                  alt={itemFirst.title}
                                />
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td
                              align="center"
                              style={{
                                fontSize: 0,
                                padding: '10px 25px',
                                wordBreak: 'break-word'
                              }}>
                              <div
                                style={{
                                  fontSize: 16,
                                  fontWeight: 500,
                                  lineHeight: '20px',
                                  textAlign: 'center',
                                  color: 'rgb(32, 34, 35)'
                                }}>
                                {itemFirst.title}
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td
                              align="center"
                              style={{
                                fontSize: 0,
                                wordBreak: 'break-word'
                              }}>
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 500,
                                  lineHeight: 1,
                                  textAlign: 'center',
                                  color: '#6d7175'
                                }}>
                                <span>{formatMoney(priceFirst, moneyFormat)}</span>

                                {!_.isNil(compareAtPriceFirst) &&
                                  compareAtPriceFirst > priceFirst && (
                                    <span
                                      style={{
                                        marginLeft: 6,
                                        textDecoration: 'line-through'
                                      }}>
                                      {formatMoney(compareAtPriceFirst, moneyFormat)}
                                    </span>
                                  )}
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {itemSecond && (
                      <div
                        className="mj-column-per-50 mj-outlook-group-fix"
                        style={{
                          fontSize: 0,
                          textAlign: 'left',
                          direction: 'ltr',
                          display: 'inline-block',
                          verticalAlign: 'top',
                          width: '100%'
                        }}>
                        <table
                          border={0}
                          cellPadding={0}
                          cellSpacing={0}
                          role="presentation"
                          style={{ verticalAlign: 'top' }}
                          width="100%">
                          <tbody>
                            <tr>
                              <td
                                align="center"
                                style={{
                                  fontSize: 0,
                                  padding: '10px 25px',
                                  wordBreak: 'break-word'
                                }}>
                                <div
                                  style={{
                                    fontSize: 13,
                                    lineHeight: 1,
                                    textAlign: 'center',
                                    color: '#000000'
                                  }}>
                                  <img
                                    src={imageMainProductSecond}
                                    style={{
                                      width: 160,
                                      height: 160,
                                      objectFit: 'contain',
                                      margin: 'auto',
                                      objectPosition: 'center'
                                    }}
                                    alt={itemSecond.title}
                                  />
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td
                                align="center"
                                style={{
                                  fontSize: 0,
                                  padding: '10px 25px',
                                  wordBreak: 'break-word'
                                }}>
                                <div
                                  style={{
                                    fontSize: 16,
                                    fontWeight: 500,
                                    lineHeight: '20px',
                                    textAlign: 'center',
                                    color: 'rgb(32, 34, 35)'
                                  }}>
                                  {itemSecond.title}
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td
                                align="center"
                                style={{
                                  fontSize: 0,
                                  wordBreak: 'break-word'
                                }}>
                                <div
                                  style={{
                                    fontSize: 14,
                                    fontWeight: 500,
                                    lineHeight: 1,
                                    textAlign: 'center',
                                    color: '#6d7175'
                                  }}>
                                  <span>{formatMoney(priceSecond, moneyFormat)}</span>

                                  {!_.isNil(compareAtPriceSecond) &&
                                    compareAtPriceSecond > priceSecond && (
                                      <span
                                        style={{
                                          marginLeft: 6,
                                          textDecoration: 'line-through'
                                        }}>
                                        {formatMoney(compareAtPriceSecond, moneyFormat)}
                                      </span>
                                    )}
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      }
    }

    return (
      <div className="preview-content">
        <p className="title-type">
          {MAP_MODAL_TITLE[recommendationType]
            ? String(i18n.translate(MAP_MODAL_TITLE[recommendationType])).toUpperCase()
            : ''}{' '}
          {String(
            i18n.translate('AdminHTML.KlaviyoIntegration.PreviewProductsModal.products')
          ).toUpperCase()}
        </p>

        <div className="product-container">
          {isLoading &&
            [1, 2, 3, 4].map(item => {
              return (
                <div className="product-item" key={item}>
                  <div className="product-item__thumbnail">
                    <SkeletonThumbnail />
                  </div>
                  <div className="product-item__info">
                    <SkeletonDisplayText />
                    <div className="product-item__info__price-wrapper">
                      <SkeletonDisplayText />
                    </div>
                  </div>
                </div>
              );
            })}

          {renderRecommendProducts}
        </div>
      </div>
    );
  }, [i18n, isLoading, moneyFormat, recommendProducts, recommendationType]);

  const htmlCode: string = useMemo(() => {
    return beautify.html(`${css}\n` + renderToString(renderCopyPreview));
  }, [renderCopyPreview]);

  const handleCopy = async () => {
    const toastMessage = i18n.translate('AdminHTML.KlaviyoIntegration.Common.copied');
    if (navigator) {
      await copyClipBoardNavigator(i18n, htmlCode, toastMessage);
    } else {
      copyClipBoard(i18n, htmlCode, toastMessage);
    }
  };

  const handleDownloadHTML = () => {
    const blob = new Blob([htmlCode], { type: 'text/plain;charset=utf-8' });
    FileSaver.saveAs(
      blob,
      `klaviyo-${i18n
        .translate(MAP_MODAL_TITLE[recommendationType])
        .toLowerCase()
        .replace(/ /g, '-')}-products.html`
    );
  };

  return (
    <Modal
      large
      open={isOpen}
      onClose={onClose}
      title={i18n.translate('AdminHTML.KlaviyoIntegration.PreviewProductsModal.title', {
        t: MAP_MODAL_TITLE[recommendationType]
          ? i18n.translate(MAP_MODAL_TITLE[recommendationType])
          : ''
      })}
      footer={null}>
      <div className="klaviyo-preview-modal">
        <div className="klaviyo-preview-modal__guideline">
          <p style={{ marginBottom: 10 }}>
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.KlaviyoIntegration.PreviewProductsModal.guideline')}
            </TextStyle>
          </p>
          <div style={{ marginBottom: 10 }}>
            <span>
              {i18n.translate('AdminHTML.KlaviyoIntegration.PreviewProductsModal.guideline_Desc', {
                learn: (
                  <Link url={URL['HowToUpgradeTurbo']} external>
                    {i18n.translate(
                      'AdminHTML.KlaviyoIntegration.PreviewProductsModal.guideline_Learn'
                    )}
                    <Icon source={ExternalMinor} />
                  </Link>
                )
              })}
            </span>
          </div>

          <p style={{ marginBottom: 10 }}>
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.KlaviyoIntegration.PreviewProductsModal.sourceCode')}
            </TextStyle>
          </p>
          <div className="code" style={{ marginBottom: 20 }}>
            <p>{htmlCode}</p>
          </div>

          <div className="klaviyo-preview-modal__actions">
            <ButtonGroup>
              <Button onClick={handleDownloadHTML}>
                {i18n.translate('AdminHTML.KlaviyoIntegration.PreviewProductsModal.downloadHTML')}
              </Button>
              <Button onClick={handleCopy}>
                {i18n.translate('AdminHTML.KlaviyoIntegration.PreviewProductsModal.copyClipboard')}
              </Button>
            </ButtonGroup>
          </div>
        </div>
        <div className="klaviyo-preview-modal__preview-content">
          <p className="title-preview">
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.Common.Action.preview')}
            </TextStyle>
          </p>

          {renderPreview}
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state, props) => {
  // Get format money, help to display price
  const moneyFormat = _.get(state.oauth.tenantApp, 'externalInfo.money_format', '');
  const recommendProducts = _.get(state.product, 'recommendProducts', {});

  return {
    moneyFormat,
    recommendProducts
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateRecommendProducts: (recommendationType, products) =>
    dispatch(productAction.updateRecommendProducts(recommendationType, products))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(PreviewProductsModal));
